import { useEffect, useMemo, useRef, useState, memo } from "react";
import { Loader2, Maximize, Minimize, Pause, Play, Volume2, VolumeX, RotateCcw, Settings } from "lucide-react";
import Hls from "hls.js";

const formatTime = (secs) => {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const normalizeBunnySource = (rawSrc) => {
  if (rawSrc === undefined || rawSrc === null) {
    return { mode: "none", url: "" };
  }

  const str = String(rawSrc).trim();
  if (!str) return { mode: "none", url: "" };

  const iframeMatch = str.match(/<iframe[^>]*src=["']([^"']+)["']/i);
  if (iframeMatch?.[1]) {
    return { mode: "iframe", url: iframeMatch[1] };
  }

  const lower = str.toLowerCase();
  if (lower.includes("/embed/") || lower.includes("bunnycdn") || lower.includes("mediadelivery")) {
    return { mode: "iframe", url: str };
  }

  if (lower.includes(".m3u8") || lower.includes("playlist.m3u8")) {
    return { mode: "hls", url: str };
  }

  if (/\.(mp4|webm|mov|mkv)(\?|$)/i.test(lower)) {
    return { mode: "file", url: str };
  }

  return { mode: "file", url: str };
};

function BunnyPlayer({ src, title, userEmail }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsTimer = useRef(null);
  const settingsRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [levels, setLevels] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState("auto");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [emailPosition, setEmailPosition] = useState({
    top: "15%",
    left: "15%",
    opacity: 0,
  });

  const source = useMemo(() => normalizeBunnySource(src), [src]);
  const videoUrl = source.mode === "hls" || source.mode === "file" ? source.url : "";
  const iframeUrl = source.mode === "iframe" ? source.url : "";
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const currentLabel = formatTime(currentTime);
  const durationLabel = formatTime(duration);

  const resetUiState = () => {
    setStatus(videoUrl ? "loading" : source.mode === "iframe" ? "ready" : "idle");
    setErrorMessage("");
    setIsPlaying(false);
    setIsMuted(false);
    setVolume(1);
    setDuration(0);
    setCurrentTime(0);
    setIsBuffering(false);
    setLevels([]);
    setSelectedQuality("auto");
    setPlaybackRate(1);
    setAutoRetryCount(0);
    setEmailPosition((prev) => ({ ...prev, opacity: 1 }));
  };

  useEffect(() => {
    resetUiState();
  }, [source.mode, source.url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (source.mode === "iframe" || source.mode === "none" || !videoUrl) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setStatus("loading");
    setErrorMessage("");
    setIsBuffering(false);

    video.controls = false;
    video.loop = false;
    video.preload = "auto";
    video.muted = isMuted;
    video.volume = volume;
    video.playbackRate = playbackRate;

    const handleLoadedMetadata = () => {
      setDuration(video.duration || 0);
      setStatus("ready");
      setIsBuffering(false);
      if (autoRetryCount !== 0) {
        setAutoRetryCount(0);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      startHideControlsTimer();
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true);
      clearHideControlsTimer();
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.duration) {
        setDuration(video.duration);
      }
    };

    const handleWaiting = () => {
      if (status === "ready") {
        setIsBuffering(true);
      }
    };

    const handleCanPlay = () => setIsBuffering(false);

    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    const handleError = () => {
      setIsPlaying(false);
      setIsBuffering(false);
      if (autoRetryCount < 2) {
        setAutoRetryCount((prev) => prev + 1);
        setReloadKey((prev) => prev + 1);
        return;
      }
      setStatus("error");
      setErrorMessage("Video failed to load. Please try again.");
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const attachSource = () => {
      if (source.mode === "hls" && Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hls.loadSource(videoUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const rawLevels = hls.levels || [];
          const mapped = rawLevels.map((lvl, index) => {
            const height = lvl.height || lvl.attrs?.RESOLUTION?.split("x")[1];
            const bitrate = lvl.bitrate || lvl.attrs?.BANDWIDTH;
            return {
              index,
              label: `${height || "L" + index}${bitrate ? ` • ${(bitrate / 1_000_000).toFixed(1)} Mbps` : ""}`,
            };
          });
          setLevels(mapped);
          setSelectedQuality("auto");
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) {
            setStatus("error");
            setErrorMessage("Network error while loading video.");
            hls.destroy();
          }
        });
        hlsRef.current = hls;
      } else if (source.mode === "hls" && video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
      } else {
        video.src = videoUrl;
      }
    };

    attachSource();

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, source.mode, reloadKey, isMuted, volume]);

  useEffect(() => {
    const handleFsChange = () => {
      const fsElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(Boolean(fsElement));
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    document.addEventListener("mozfullscreenchange", handleFsChange);
    document.addEventListener("MSFullscreenChange", handleFsChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
      document.removeEventListener("mozfullscreenchange", handleFsChange);
      document.removeEventListener("MSFullscreenChange", handleFsChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettings && settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  useEffect(() => {
    return () => clearHideControlsTimer();
  }, []);

  useEffect(() => {
    if (!videoUrl) return;

    const handleKey = (event) => {
      if (["INPUT", "TEXTAREA"].includes(event.target.tagName)) return;
      switch (event.key.toLowerCase()) {
        case " ":
        case "k":
          event.preventDefault();
          togglePlay(event);
          break;
        case "m":
          toggleMute(event);
          break;
        case "f":
          toggleFullscreen(event);
          break;
        case "arrowleft":
          event.preventDefault();
          seekRelative(-5);
          break;
        case "arrowright":
          event.preventDefault();
          seekRelative(5);
          break;
        case "arrowup":
          event.preventDefault();
          changeVolume(Math.min(1, volume + 0.1));
          break;
        case "arrowdown":
          event.preventDefault();
          changeVolume(Math.max(0, volume - 0.1));
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [videoUrl, volume]);

  const clearHideControlsTimer = () => {
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
      hideControlsTimer.current = null;
    }
  };

  const startHideControlsTimer = () => {
    clearHideControlsTimer();
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2800);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (isPlaying) {
      startHideControlsTimer();
    }
  };

  useEffect(() => {
    if (!userEmail) return;
    const interval = setInterval(() => {
      setEmailPosition({
        top: `${Math.floor(Math.random() * 70) + 10}%`,
        left: `${Math.floor(Math.random() * 70) + 10}%`,
        opacity: 0.9,
      });
      setTimeout(() => {
        setEmailPosition((prev) => ({ ...prev, opacity: 1 }));
      }, 2200);
    }, 6500);
    return () => clearInterval(interval);
  }, [userEmail]);

  const togglePlay = (event) => {
    if (event) event.stopPropagation();
    const video = videoRef.current;
    if (!video || source.mode === "iframe") return;

    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          startHideControlsTimer();
        })
        .catch(() => {
          setStatus("error");
          setErrorMessage("Unable to start playback.");
        });
    } else {
      video.pause();
    }
  };

  const toggleMute = (event) => {
    if (event) event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (!video.muted && video.volume === 0) {
      video.volume = 0.4;
      setVolume(0.4);
    }
  };

  const changeVolume = (value) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(1, Math.max(0, value));
    video.volume = next;
    video.muted = next === 0;
    setVolume(next);
    setIsMuted(video.muted);
  };

  const handleSeek = (value) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const pct = Math.min(100, Math.max(0, Number(value)));
    video.currentTime = (pct / 100) * duration;
    setCurrentTime(video.currentTime);
  };

  const seekRelative = (deltaSeconds) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Math.min(Math.max(video.currentTime + deltaSeconds, 0), video.duration || video.currentTime);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const toggleFullscreen = (event) => {
    if (event) event.stopPropagation();
    const target = containerRef.current;
    if (!target) return;

    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (!fsElement) {
      if (target.requestFullscreen) target.requestFullscreen();
      else if (target.webkitRequestFullscreen) target.webkitRequestFullscreen();
      else if (target.mozRequestFullScreen) target.mozRequestFullScreen();
      else if (target.msRequestFullscreen) target.msRequestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  const handleRetry = (event) => {
    if (event) event.stopPropagation();
    setAutoRetryCount(0);
    setReloadKey((prev) => prev + 1);
    setStatus("loading");
  };

  const handleQualityChange = (event) => {
    const value = event.target.value;
    setSelectedQuality(value);
    const hls = hlsRef.current;
    if (!hls) return;
    if (value === "auto") {
      hls.currentLevel = -1;
      return;
    }
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      hls.currentLevel = parsed;
    }
  };

  const handleSpeedChange = (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isNaN(value)) return;
    setPlaybackRate(value);
  };

  if (!videoUrl && !iframeUrl) {
    return (
      <div
        style={{
          width: "100%",
          padding: "40px",
          background: "#0f1117",
          color: "white",
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        No video source available.
      </div>
    );
  }

  const containerStyle = {
    width: "100%",
    position: "relative",
    borderRadius: "0px",
    background: "radial-gradient(circle at 15% 20%, rgba(96,165,250,0.2), transparent 45%), radial-gradient(circle at 80% 0%, rgba(236,72,153,0.18), transparent 55%), #020617",
    aspectRatio: isFullscreen ? "auto" : "16 / 9",
    overflow: "hidden",
    boxShadow: "0 35px 80px rgba(5,10,25,0.65)",
    border: isFullscreen ? "none" : "1px solid rgba(255,255,255,0.05)",
  };

  const blurOrb = (styleOverride) => ({
    position: "absolute",
    borderRadius: 0,
    filter: "blur(60px)",
    opacity: 0.65,
    pointerEvents: "none",
    ...styleOverride,
  });

  if (iframeUrl) {
    return (
      <div className="custom-bunny-player" style={{ width: "100%" }}>
        <div ref={containerRef} style={containerStyle}>
          {title ? (
            <div
              style={{
                position: "absolute",
                top: 16,
                left: 20,
                color: "rgba(255,255,255,0.85)",
                fontWeight: 600,
                zIndex: 5,
              }}
            >
              {title}
            </div>
          ) : null}
          <iframe
            title={title || "Course video"}
            src={iframeUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              border: "0",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="custom-bunny-player" style={{ width: "100%" }}>
      <div
        ref={containerRef}
        style={containerStyle}
        className="relative custom-bunny-player-container bg-black"
        onMouseMove={handleMouseMove}
        onClick={togglePlay}
      >
        {title ? (
          <div
            style={{
              position: "absolute",
              top: 18,
              left: 20,
              right: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              zIndex: 6,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 18px",
                borderRadius: "999px",
                background: "linear-gradient(120deg, rgba(59,130,246,0.9), rgba(147,51,234,0.85))",
                color: "white",
                fontWeight: 600,
                letterSpacing: "0.02em",
                boxShadow: "0 12px 25px rgba(99,102,241,0.4)",
              }}
            >
              <span style={{ fontSize: "13px" }}>{title}</span>
              <span
                style={{
                  fontSize: "9px",
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  padding: "4px 10px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              >
                Secure Player
              </span>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "14px",
                background: "rgba(2,6,23,0.55)",
                color: "rgba(255,255,255,0.85)",
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 8px 16px rgba(2,6,23,0.5)",
              }}
            >
              <span>HD</span>
              <span style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(255,255,255,0.5)" }} />
              <span>{levels.length ? "Adaptive" : "Standard"}</span>
            </div>
          </div>
        ) : null}

        <video
          ref={videoRef}
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            background: "black",
          }}
          onClick={(event) => event.stopPropagation()}
        />

        <div
          style={blurOrb({
            top: "-10%",
            left: "-5%",
            width: "45%",
            height: "60%",
            background: "rgba(59,130,246,0.45)",
          })}
        />
        <div
          style={blurOrb({
            top: "-15%",
            right: "-10%",
            width: "40%",
            height: "55%",
            background: "rgba(236,72,153,0.35)",
          })}
        />
        <div
          style={blurOrb({
            bottom: "-25%",
            left: "-15%",
            width: "55%",
            height: "65%",
            background: "rgba(56,189,248,0.25)",
          })}
        />

        {(status === "loading" || isBuffering) && (
          <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-black/40">
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white gap-4 px-6 text-center">
            <p>{errorMessage}</p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/30 hover:bg-white/20 transition"
            >
              <RotateCcw size={16} />
              Retry
            </button>
          </div>
        )}

        {!isPlaying && status === "ready" && !isBuffering && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button
              onClick={togglePlay}
              className="pointer-events-auto inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 text-white shadow-2xl"
            >
              <Play size={28} fill="currentColor" className="ml-1" />
            </button>
          </div>
        )}

        {userEmail && (
          <div
            style={{
              position: "absolute",
              top: emailPosition.top,
              left: emailPosition.left,
              transform: "translate(-50%, -50%)",
              padding: "6px 14px",
              borderRadius: "999px",
              background: "rgba(0,0,0,0.65)",
              color: "white",
              fontSize: "12px",
              letterSpacing: "0.03em",
              pointerEvents: "none",
              opacity: emailPosition.opacity,
              transition: "opacity 0.4s ease",
              backdropFilter: "blur(4px)",
              border: "1px solid rgba(255,255,255,0.15)",
              zIndex: 6,
            }}
          >
            {userEmail}
          </div>
        )}

        <div
          className={`absolute inset-0 flex flex-col justify-end transition-all duration-300 h-[70vh] md:h-auto md:!top-[-70px] ${showControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
            }`}
          onClick={(event) => event.stopPropagation()}
          style={{ padding: isFullscreen ? "20px 24px 26px" : "10px 14px 16px" }}
        >
          {/* Gradient backdrop */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(2,6,23,0.6) 50%, rgba(2,6,23,0.97) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* ── Settings popup (mobile) ── */}
          {showSettings && (
            <div
              ref={settingsRef}
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-[70px] right-4 bg-[#0a0f1e]/95 backdrop-blur-2xl border border-white/10 rounded-3xl p-5 z-50 min-w-[220px] max-h-[350px] overflow-y-auto shadow-[0_20px_50px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in slide-in-from-bottom-4 duration-300"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-3">
                <span className="text-white font-black text-[10px] uppercase tracking-[0.2em]">Video Settings</span>
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
              </div>

              {/* Speed Section */}
              <div className="mb-6">
                <div className="text-slate-400 font-bold text-[9px] uppercase tracking-widest mb-3 flex items-center gap-2">
                  <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                  Playback Speed
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); setPlaybackRate(s); }}
                      className={`py-2 px-1 rounded-xl text-[11px] font-bold transition-all duration-200 border ${playbackRate === s
                          ? "bg-blue-600 border-blue-400 text-white shadow-[0_5px_15px_rgba(59,130,246,0.4)]"
                          : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                        }`}
                    >
                      {s === 1 ? "Normal" : `${s}x`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quality Section */}
              {levels.length > 0 && (
                <div>
                  <div className="text-slate-400 font-bold text-[9px] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <div className="w-1 h-3 bg-purple-500 rounded-full"></div>
                    Resolution
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {[{ index: "auto", label: "Auto Detection" }, ...levels.map((l) => ({ index: l.index, label: l.label }))].map((q) => (
                      <button
                        key={q.index}
                        onClick={(e) => { e.stopPropagation(); handleQualityChange({ target: { value: String(q.index) } }); }}
                        className={`flex items-center justify-between py-2.5 px-4 rounded-xl text-[12px] font-bold transition-all duration-200 border ${String(selectedQuality) === String(q.index)
                            ? "bg-purple-600 border-purple-400 text-white shadow-[0_5px_15px_rgba(147,51,234,0.4)]"
                            : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                          }`}
                      >
                        <span>{q.label}</span>
                        {String(selectedQuality) === String(q.index) && (
                          <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}


          <div className="custom-players-web-controllers md:!top-[-70px]  " style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>

            {/* ── ROW 1: Progress bar ── ALWAYS FULL WIDTH ── */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
              <span style={{ color: "white", fontFamily: "monospace", fontSize: "11px", whiteSpace: "nowrap", flexShrink: 0, background: "rgba(255,255,255,0.1)", padding: "3px 8px", borderRadius: "999px" }}>
                {currentLabel}
              </span>
              {/* flex:1 + minWidth:0 prevents overflow on any screen */}
              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={progress}
                onChange={(e) => handleSeek(e.target.value)}
                className="custom-slider"
                style={{ flex: 1, minWidth: 0 }}
              />
              <span style={{ color: "rgba(255,255,255,0.55)", fontFamily: "monospace", fontSize: "11px", whiteSpace: "nowrap", flexShrink: 0 }}>
                {durationLabel}
              </span>
            </div>

            {/* ── ROW 2: Controls ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", width: "100%" }}>

              {/* LEFT: -10s, Play/Pause, +10s */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  onClick={(e) => { e.stopPropagation(); seekRelative(-10); }}
                  className="hidden sm:block"
                  style={{ padding: "5px 10px", borderRadius: "999px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", color: "white", fontSize: "11px", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  -10s
                </button>

                <button
                  onClick={togglePlay}
                  style={{ width: 42, height: 42, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg, #3b82f6, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(59,130,246,0.45)" }}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} fill="currentColor" style={{ marginLeft: 2 }} />}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); seekRelative(10); }}
                  className="hidden sm:block"
                  style={{ padding: "5px 10px", borderRadius: "999px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", color: "white", fontSize: "11px", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  +10s
                </button>

                {/* Seek buttons on mobile — compact */}
                <div className="flex sm:hidden" style={{ gap: 4 }}>
                  <button onClick={(e) => { e.stopPropagation(); seekRelative(-10); }} style={{ padding: "4px 8px", borderRadius: "999px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", color: "white", fontSize: "10px", cursor: "pointer" }}>-10s</button>
                  <button onClick={(e) => { e.stopPropagation(); seekRelative(10); }} style={{ padding: "4px 8px", borderRadius: "999px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", color: "white", fontSize: "10px", cursor: "pointer" }}>+10s</button>
                </div>
              </div>

              {/* RIGHT: Mute + Volume (sm+) + Settings popup btn + Quality/Speed (sm+) + Fullscreen */}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>

                {/* Mute */}
                <button
                  onClick={toggleMute}
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}
                >
                  {isMuted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>

                {/* Volume slider — desktop only */}
                <input
                  type="range"
                  min={0} max={1} step={0.05}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  className="custom-slider hidden sm:block"
                  style={{ width: 72 }}
                />

                {/* ── Desktop: inline Speed + Quality selects ── */}
                <div className="hidden sm:flex" style={{ alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", padding: "6px 12px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.1)" }}>
                  {levels.length > 0 && (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>Q</span>
                      <select
                        value={selectedQuality}
                        onChange={handleQualityChange}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-black/40 border border-white/30 rounded px-2 py-1 text-white text-xs focus:outline-none"
                      >
                        <option value="auto">Auto</option>
                        {levels.map((lvl) => <option key={lvl.index} value={lvl.index}>{lvl.label}</option>)}
                      </select>
                    </>
                  )}
                  <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>×</span>
                  <select
                    value={playbackRate}
                    onChange={handleSpeedChange}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-black/40 border border-white/30 rounded px-2 py-1 text-white text-xs focus:outline-none"
                  >
                    <option value={0.5}>0.5×</option>
                    <option value={0.75}>0.75×</option>
                    <option value={1}>1×</option>
                    <option value={1.25}>1.25×</option>
                    <option value={1.5}>1.5×</option>
                    <option value={2}>2×</option>
                  </select>
                </div>

                {/* ── Mobile: settings button ── */}
                <button
                  className="flex sm:hidden"
                  onClick={(e) => { e.stopPropagation(); setShowSettings((v) => !v); }}
                  style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: showSettings ? "rgba(99,102,241,0.7)" : "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  <Settings size={16} />
                </button>

                {/* Fullscreen */}
                <button
                  onClick={toggleFullscreen}
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}
                >
                  {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                </button>
              </div>
            </div>
          </div>

          <style>{`
            .custom-slider {
              appearance: none;
              -webkit-appearance: none;
              height: 4px;
              background: linear-gradient(90deg, rgba(59,130,246,0.85), rgba(147,51,234,0.85));
              border-radius: 999px;
              outline: none;
              cursor: pointer;
            }
            .custom-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 14px; height: 14px;
              border-radius: 50%;
              background: white;
              border: 2px solid rgba(59,130,246,0.9);
              box-shadow: 0 2px 8px rgba(59,130,246,0.5);
              cursor: pointer;
            }
            .custom-slider::-moz-range-thumb {
              width: 14px; height: 14px;
              border-radius: 50%;
              background: white;
              border: 2px solid rgba(59,130,246,0.9);
              cursor: pointer;
            }
            @media (max-width: 640px) {
              .custom-slider { height: 3px; }
              .custom-slider::-webkit-slider-thumb { width: 12px; height: 12px; }
              .custom-slider::-moz-range-thumb { width: 12px; height: 12px; }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
}

export default memo(BunnyPlayer);


